/**
 * Evolução do saldo consolidado do investidor (saldo_consolidado_investidor) — linha diária.
 */
import { useMemo, useState } from 'react'
import { formatCurrency } from '../../../../utils/obraAnalytics'

const CHART = { width: 640, height: 220, padX: 48, padY: 28 }

const EvolucaoContaChart = ({ title = 'Evolução da conta', serie = [], refDia = null }) => {
    const [hover, setHover] = useState(null)

    const pontos = useMemo(() => (serie || []).map((r) => ({
        dia: r.dia,
        label: r.label ?? r.dia?.slice(8),
        saldo: Number(r.saldo ?? r.saldo_conta ?? 0),
        futuro: Boolean(r.futuro),
    })), [serie])

    const layout = useMemo(() => {
        if (!pontos.length) return null
        const valores = pontos.map((p) => p.saldo)
        const min = Math.min(...valores, 0)
        const max = Math.max(...valores, 0)
        const span = max - min || 1
        const innerW = CHART.width - CHART.padX * 2
        const innerH = CHART.height - CHART.padY * 2
        const step = pontos.length > 1 ? innerW / (pontos.length - 1) : 0
        const toY = (v) => CHART.padY + innerH - ((v - min) / span) * innerH
        const zeroY = toY(0)

        const pts = pontos.map((p, idx) => ({
            ...p,
            x: CHART.padX + idx * step,
            y: toY(p.saldo),
        }))

        const refIdx = refDia ? pts.findIndex((p) => p.dia === refDia) : -1

        return {
            pts,
            line: pts.map((p) => `${p.x},${p.y}`).join(' '),
            min,
            max,
            zeroY,
            refIdx,
            refX: refIdx >= 0 ? pts[refIdx].x : null,
        }
    }, [pontos, refDia])

    if (!layout) {
        return (
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                <p className="text-xs text-gray-400 mt-1">Saldo consolidado do investidor (ERP), dia a dia</p>
                <div className="flex items-center justify-center h-44 mt-4">
                    <p className="text-xs text-gray-400">Sem dados no período.</p>
                </div>
            </div>
        )
    }

    const active = hover != null ? layout.pts[hover] : null
    const temSaldo = pontos.some((p) => p.saldo !== 0)

    return (
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4 min-h-[64px]">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-400 mt-1">
                        Saldo operacional acumulado no período
                    </p>
                </div>
                <div className="text-right text-xs min-w-[120px] min-h-[48px]">
                    {active ? (
                        <>
                            <p className="font-bold text-gray-800">{active.label}</p>
                            <p className={`font-semibold ${active.saldo >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                {formatCurrency(active.saldo)}
                            </p>
                            {active.futuro && <p className="text-amber-600">Projeção / futuro</p>}
                        </>
                    ) : (
                        <p className="text-gray-400 pt-2">Passe o mouse</p>
                    )}
                </div>
            </div>

            {!temSaldo ? (
                <div className="flex items-center justify-center h-44">
                    <p className="text-xs text-gray-400">Sem saldo operacional no período.</p>
                </div>
            ) : (
                <div className="relative overflow-x-auto">
                    <svg width={CHART.width} height={CHART.height} viewBox={`0 0 ${CHART.width} ${CHART.height}`} className="mx-auto min-w-[320px]">
                        <line x1={CHART.padX} y1={layout.zeroY} x2={CHART.width - CHART.padX} y2={layout.zeroY} stroke="#e5e7eb" strokeWidth="1" />
                        {layout.refX != null && (
                            <line x1={layout.refX} y1={CHART.padY} x2={layout.refX} y2={CHART.height - CHART.padY} stroke="#fbbf24" strokeDasharray="4 4" strokeWidth="1.5" />
                        )}
                        <polyline fill="none" stroke="#be123c" strokeWidth="2.5" points={layout.line} strokeLinecap="round" strokeLinejoin="round" />
                        {layout.pts.map((p, idx) => (
                            <g key={`${p.dia}-${idx}`}>
                                <circle cx={p.x} cy={p.y} r="10" fill="transparent" className="cursor-pointer" onMouseEnter={() => setHover(idx)} onMouseLeave={() => setHover(null)} />
                                <circle cx={p.x} cy={p.y} r={hover === idx ? 5 : 3} fill={p.futuro ? '#f59e0b' : '#be123c'} className="pointer-events-none" />
                            </g>
                        ))}
                    </svg>
                </div>
            )}
        </div>
    )
}

export default EvolucaoContaChart
